import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { EducationalPopover } from '@liveauctioneers/hammer-ui-core/educationalPopover';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close, MagnifyingGlass } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, BodySecondary, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOneTimePopoverWasDismissed } from '@/hooks/useOneTimePopoverWasDismissed';
import { useTrackSimilarCTAClicked } from '@/utils/amplitude/analytics/similarItems/trackSimilarCTAClicked.analytics';
import { useSimilarItemsCTAItemFields } from '@/utils/amplitude/analytics/similarItems/useSimilarItemsCTAItemFields';

type SeeSimilarItemsButtonExperimentProps = {
    isIconCTA: boolean;
    isMobile: boolean;
    itemId: number;
    position: number;
    shouldShowEducationalPopover?: boolean;
};

const EDUCATIONAL_POPOVER_COOKIE_NAME = 'newSimilarItemsEducationalPopoverDismissed';

export const SeeSimilarItemsButtonExperiment = ({
    isIconCTA,
    isMobile,
    itemId,
    position,
    shouldShowEducationalPopover,
}: SeeSimilarItemsButtonExperimentProps) => {
    const navigate = useNavigate();
    const seeSimilarItemsId = `see-similar-items-${itemId}`;
    const trackSimilarCTAClicked = useTrackSimilarCTAClicked();
    const { auctionType, houseId } = useSimilarItemsCTAItemFields(itemId);

    const { handleDismissPopover, showPopover } = useOneTimePopoverWasDismissed(
        EDUCATIONAL_POPOVER_COOKIE_NAME,
        shouldShowEducationalPopover
    );

    const handleSeeSimilarItemsClick = useCallback(() => {
        trackSimilarCTAClicked({
            ...(auctionType !== undefined && { auctionType }),
            houseId,
            itemId,
            position,
        });
        navigate(`/similar/${itemId}`);
    }, [auctionType, houseId, itemId, navigate, position, trackSimilarCTAClicked]);

    const handleOnMouseEnterSimilarItemsButton = useCallback(() => {
        handleDismissPopover();
    }, [handleDismissPopover]);

    return (
        <>
            {showPopover && (
                <EducationalPopover
                    anchorSelect={`#${seeSimilarItemsId}`}
                    className="!w-[300px] !rounded-none"
                    isInverse
                    isOpen
                    pages={[
                        <>
                            <div className="flex items-center justify-between pb-xs">
                                <H5 className="text-text-inverse">
                                    <FormattedMessage id="searchResults.searchPageItemCard.educationalPopover.title" />
                                </H5>
                                <IconButton
                                    className="!h-[20px] !min-h-[20px] !w-[20px] !min-w-[20px] text-text-inverse hover:text-text-inverse"
                                    icon={Close}
                                    onClick={handleDismissPopover}
                                    size="sm"
                                    style="neutral"
                                />
                            </div>
                            <BodyPrimary className="text-primary">
                                <FormattedMessage id="searchResults.searchPageItemCard.educationalPopover.description" />
                            </BodyPrimary>
                        </>,
                    ]}
                    place="bottom"
                    style={{
                        maxWidth: '300px',
                    }}
                />
            )}

            <span className="absolute bottom-2xs left-2xs z-[8] smMin:bottom-2 smMin:left-2">
                {!isMobile && (
                    <Tooltip
                        anchorSelect={`#${seeSimilarItemsId}`}
                        content="See Similar Items"
                        place="top"
                        positionStrategy="fixed"
                    />
                )}

                {isIconCTA && (
                    <IconButton
                        icon={MagnifyingGlass}
                        id={seeSimilarItemsId}
                        onClick={handleSeeSimilarItemsClick}
                        onMouseEnter={handleOnMouseEnterSimilarItemsButton}
                        size="sm"
                        style="neutral"
                        variant="outlined"
                    />
                )}

                {!isIconCTA && !isMobile && (
                    <Button
                        iconLeft={MagnifyingGlass}
                        id={seeSimilarItemsId}
                        onClick={handleSeeSimilarItemsClick}
                        onMouseEnter={handleOnMouseEnterSimilarItemsButton}
                        size="sm"
                        style="neutral"
                        variant="outlined"
                    >
                        <FormattedMessage id="searchResults.searchPageItemCard.similarItemsButton" />
                    </Button>
                )}

                {!isIconCTA && isMobile && (
                    <span className="inline-block rounded-lg border border-solid border-border-tertiary bg-background-primary/70 backdrop-blur-sm">
                        <Button
                            className="h-lg px-xs"
                            iconLeft={MagnifyingGlass}
                            id={seeSimilarItemsId}
                            onClick={handleSeeSimilarItemsClick}
                            onMouseEnter={handleOnMouseEnterSimilarItemsButton}
                            size="sm"
                            style="neutral"
                            variant="transparent"
                        >
                            <BodySecondary className="capitalize text-text-secondary">
                                <FormattedMessage id="searchResults.searchPageItemCard.similarItemsButton" />
                            </BodySecondary>
                        </Button>
                    </span>
                )}
            </span>
        </>
    );
};
